import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AdminUser, PasswordReset, SentInvite, UserReachSummary, adminCreateUser, adminListUsers, adminMe,
  adminResendInvite, adminResetUserPassword, adminUpdateUser, getBilling, getSsoSettings,
  isInvitePending, resetUserTwoFactor, setUserSsoExemptApi,
} from '../../api/admin';
import { ReachPanel } from './ReachPanel';
import type { AccountSso, IdentityProvider } from '../../shared/types';
import { PlanLimitBody, planLimitOf } from '../../api/limits';
import PlanLimitBlock from '../../components/PlanLimitBlock';
import { Modal, RowMenu, TableCard } from '../../components/fs';
import {
  ChangePasswordModal, InviteLinkModal, ResetPasswordModal, invitedMessage,
} from '../../components/PasswordDialogs';
import { ago } from './SsoShared';
import { useToast } from '../../components/Toast';
import { PHONE, useMediaQuery } from '../../shared/media';

/** Who last set a password, as the Sign-in cell says it. */
const PASSWORD_ACTOR: Record<string, string> = {
  self: 'changed',
  account_admin: 'reset by an admin',
  platform: 'reset by support',
  reset_link: 'set from a reset link',
  invite: 'set when they accepted their invitation',
};

/** ONE editor at a time (rule 6). */
type Editor =
  | { kind: 'add' }
  | { kind: 'mine' }
  | { kind: 'role' | 'active' | 'password' | 'reach' | 'reset2fa'; userId: number }
  | null;

/**
 * The rungs, and the job each one does.
 *
 * The blurbs are the same sentences the server's lib/accountRoles.ts carries,
 * because the role select and the API refusal should not describe the ladder
 * differently.
 */
const ROLES = [
  { value: 'owner', label: 'Owner', blurb: 'Pays the bill. Everything an Admin can do, plus billing and the plan.' },
  { value: 'admin', label: 'Admin', blurb: 'Runs the workspace: users, groups, folders, integrations, every form.' },
  { value: 'member', label: 'Member', blurb: 'Does the work. Sees only the forms and folders shared with them.' },
] as const;

type Role = typeof ROLES[number]['value'];

const roleLabel = (r: string) => ROLES.find((x) => x.value === r)?.label || r;

/**
 * Users, with how each person actually gets in.
 *
 * The Sign-in column matters most while an account requires SSO: a user with
 * only a password can no longer sign in, and saying so plainly here is the
 * difference between an obvious fix and a silent lockout.
 */
export default function UsersAdmin() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  /** How many forms each person can actually reach — the Access column. */
  const [reach, setReach] = useState<UserReachSummary[]>([]);
  const [totalForms, setTotalForms] = useState(0);
  /** R10: the 2FA and Groups columns */
  const [facts, setFacts] = useState<{ userId: number; twoFactor: boolean; groups: string[] }[]>([]);
  const [sso, setSso] = useState<AccountSso | null>(null);
  const [providers, setProviders] = useState<IdentityProvider[]>([]);
  const [me, setMe] = useState<{ id: number; email: string; role: string; isPlatformAdmin: boolean } | null>(null);
  /** false on an install with no SMTP — no screen may offer to email a link. */
  const [canEmail, setCanEmail] = useState(false);
  /** the seat ceiling, when the caller may read billing; null = unknown */
  const [seatLimit, setSeatLimitInfo] = useState<number | null>(null);
  const [error, setError] = useState('');
  const toast = useToast();
  const [editor, setEditor] = useState<Editor>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  /** set only when an invitation could not be emailed — see InviteLinkModal */
  const [handOver, setHandOver] = useState<SentInvite | null>(null);
  /** a card per person instead of the 7-column table (§4G G2) */
  const phone = useMediaQuery(PHONE);

  const load = () => adminListUsers()
    .then((r) => { setUsers(r.users); setReach(r.reach || []); setTotalForms(r.totalForms || 0); setFacts(r.facts || []); })
    .catch((e) => setError(e.message));
  useEffect(() => {
    load();
    getSsoSettings().then((r) => { setSso(r.sso); setProviders(r.providers); }).catch(() => undefined);
    adminMe()
      .then((r) => {
        setMe({ id: r.admin.id, email: r.admin.email, role: r.admin.role, isPlatformAdmin: r.isPlatformAdmin });
        setCanEmail(!!r.mailConfigured);
      })
      .catch(() => undefined);
    // Billing is superadmin-only; for everyone else the subline counts users
    // without claiming a ceiling it cannot know.
    getBilling().then((r) => setSeatLimitInfo(r.entitlement.limits.users ?? null)).catch(() => undefined);
  }, []);

  const ssoRequired = sso?.mode === 'required';
  const teamProvider = providers.find((p) => p.id === sso?.providerId) || null;
  const allowed = sso?.allowedDomains || [];
  const canManage = !!me && (me.role === 'owner' || me.role === 'admin' || me.isPlatformAdmin);
  // Only an Owner may hand out Owner, or touch the one already there — an
  // Admin who could do either could rewrite who pays for the account.
  const isOwner = !!me && (me.role === 'owner' || me.isPlatformAdmin);
  const reachOf = (id: number) => reach.find((r) => r.userId === id) || null;
  const factsOf = (id: number) => facts.find((f) => f.userId === id) || null;
  /** "On" / "Off", or a dash for somebody who has not signed in yet (nothing to protect). */
  const twoFactorCell = (u: AdminUser) => {
    const f = factsOf(u.id);
    if (!f || isInvitePending(u)) return <span className="st-muted">—</span>;
    return f.twoFactor ? <span className="st-state ok">On</span> : <span className="st-state off">Off</span>;
  };
  const groupsCell = (u: AdminUser) => {
    const g = factsOf(u.id)?.groups || [];
    return g.length ? g.join(', ') : <span className="st-muted">—</span>;
  };

  async function write(fn: () => Promise<unknown>, doneMsg?: string) {
    setError('');
    try {
      await fn();
      setEditor(null);
      if (doneMsg) toast(doneMsg);
      load();
    } catch (e: any) { setError(e.message); }
  }

  /**
   * Send somebody's invitation again.
   *
   * Not routed through `write`, because the interesting outcome is not "it
   * worked" — it is the link itself when the mail could not go out, which
   * needs a dialog rather than a green line.
   */
  async function resend(u: AdminUser) {
    setError(''); setOpenMenuId(null);
    try {
      const r = await adminResendInvite(u.id);
      if (r.invite.emailed) toast(invitedMessage(r.invite));
      else setHandOver(r.invite);
      load();
    } catch (e: any) { setError(e.message); }
  }

  async function toggleExempt(u: AdminUser) {
    setError('');
    try {
      await setUserSsoExemptApi(u.id, !u.sso_exempt);
      load();
    } catch (e: any) { setError(e.message); }
  }

  const activeCount = users.filter((u) => u.active !== false).length;
  const editing = editor && 'userId' in editor ? users.find((u) => u.id === editor.userId) || null : null;

  /**
   * The row's menu, built once and rendered by whichever list is on screen
   * (G2). A phone gets a card per person instead of a 7-column table, and the
   * actions have to be the same actions — so they are literally the same ones.
   */
  const menuFor = (u: AdminUser) => (canManage || me?.id === u.id) && (
    <RowMenu
      open={openMenuId === u.id}
      onToggle={(open) => setOpenMenuId(open ? u.id : null)}
      label={u.name || u.email}
    >
      {canManage && (
        <button onClick={() => setEditor({ kind: 'reach', userId: u.id })}>
          See what they can reach…
        </button>
      )}
      {canManage && me?.id !== u.id && (isOwner || u.role !== 'owner')
        && <button onClick={() => setEditor({ kind: 'role', userId: u.id })}>Change role…</button>}
      {me?.id === u.id && <button onClick={() => setEditor({ kind: 'mine' })}>Change password…</button>}
      {/* Somebody who has never set a password is being invited, not reset —
          and telling them their password was reset would be a sentence about
          nothing. */}
      {me?.id !== u.id && canManage && (isInvitePending(u)
        ? <button onClick={() => resend(u)}>Resend invitation</button>
        : <button onClick={() => setEditor({ kind: 'password', userId: u.id })}>Reset password…</button>)}
      {/* The lost-phone door (T2). Reset, never read: it clears their second
          factor and their sessions, and they hear about it by email. */}
      {me?.id !== u.id && canManage && !isInvitePending(u)
        && <button onClick={() => setEditor({ kind: 'reset2fa', userId: u.id })}>Reset two-factor…</button>}
      {canManage && (
        <button
          title="A break-glass user keeps a working password even while SSO is required"
          onClick={() => toggleExempt(u)}
        >
          {u.sso_exempt ? 'Remove break-glass' : 'Make break-glass'}
        </button>
      )}
      {canManage && me?.id !== u.id && (
        <>
          <div className="rule" />
          {u.active === false ? (
            <button onClick={() => setEditor({ kind: 'active', userId: u.id })}>Reactivate…</button>
          ) : (
            <button className="danger" onClick={() => setEditor({ kind: 'active', userId: u.id })}>Deactivate…</button>
          )}
        </>
      )}
    </RowMenu>
  );

  return (
    <div className="st-stack">
      <div className="fs-page-head">
        <div className="text">
          <h1>Users</h1>
          <div className="fs-page-sub">
            {seatLimit !== null
              ? <>{activeCount} of {seatLimit} seats used</>
              : <>{activeCount} user{activeCount === 1 ? '' : 's'}</>}
            {ssoRequired && (
              <>
                {' · SSO required for this account — new people are created on first '}
                {teamProvider?.name || 'IdP'}{' sign-in '}
                <Link to="/admin/settings/access">Change team sign-in →</Link>
              </>
            )}
          </div>
        </div>
        {canManage && (
          <button className="fs-btn primary" onClick={() => setEditor({ kind: 'add' })}>
            Invite user
          </button>
        )}
      </div>
      {error && <div className="ad-err">{error}</div>}

      <TableCard
        footer={(
          <span>
            Owners and Admins can reach every form in the account, whatever is shared.
            One signed-in break-glass admin must keep a password, or a broken IdP locks the account out.{' '}
            <Link to="/admin/settings/access">Change team sign-in →</Link>
          </span>
        )}
      >
        {phone ? (
          <div className="us-cards">
            {users.map((u) => {
              const r = reachOf(u.id);
              return (
                <div className={`us-card${u.active === false ? ' off' : ''}`} key={u.id}>
                  <div className="head">
                    <div className="who">
                      <div className="name">
                        {u.name || u.email}
                        {me?.id === u.id && <span className="you"> · you</span>}
                      </div>
                      {u.name && <div className="mail">{u.email}</div>}
                    </div>
                    {menuFor(u)}
                  </div>
                  <div className="pills">
                    <span className="fs-pill identity">{roleLabel(u.role)}</span>
                    <span className={`fs-pill ${u.active !== false ? 'ok' : 'neutral'}`}>
                      {u.active !== false ? 'active' : 'disabled'}
                    </span>
                    <SignInCell
                      user={u} ssoRequired={!!ssoRequired}
                      providerName={teamProvider?.name} allowedDomains={allowed}
                    />
                  </div>
                  <div className="facts">
                    {r?.everything ? 'every form' : r && (r.forms || r.folders)
                      ? <a href="#" onClick={(e) => { e.preventDefault(); setEditor({ kind: 'reach', userId: u.id }); }}>
                        {r.forms} of {totalForms} form{r.forms === 1 ? '' : 's'} · {r.folders} folder{r.folders === 1 ? '' : 's'}
                      </a>
                      : <a href="#" onClick={(e) => { e.preventDefault(); setEditor({ kind: 'reach', userId: u.id }); }}>
                        nothing shared yet
                      </a>}
                    {' · '}
                    {u.last_signin_at ? `last sign-in ${ago(u.last_signin_at)}` : 'never signed in'}
                    {isInvitePending(u) && <> · invited {ago(u.invited_at!)}, not accepted</>}
                    {factsOf(u.id) && !isInvitePending(u) && <> · 2FA {factsOf(u.id)!.twoFactor ? 'on' : 'off'}</>}
                    {!!factsOf(u.id)?.groups.length && <> · {factsOf(u.id)!.groups.join(', ')}</>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
        <table className="fs-table">
          <thead>
            <tr>
              <th>Person</th><th>Role</th><th>Groups</th><th>2FA</th><th>Access</th><th>Sign-in</th><th>Last sign-in</th><th>Status</th>
              <th style={{ width: 52 }} />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} style={u.active === false ? { opacity: 0.6 } : undefined}>
                <td>
                  <div style={{ fontWeight: 600 }}>
                    {u.name || u.email}
                    {me?.id === u.id && <span style={{ color: 'var(--text-faint)', fontWeight: 400, fontSize: 12 }}> · you</span>}
                  </div>
                  {u.name && <div className="fs-cell-sub faint">{u.email}</div>}
                </td>
                <td>
                  {/* A rung is not a status, so all three are identity —
                      never ok/warn/danger, which would rank the people. */}
                  <span className="fs-pill identity">{roleLabel(u.role)}</span>
                  <div className="fs-cell-sub">{ROLES.find((r) => r.value === u.role)?.blurb.split('.')[0]}</div>
                </td>
                <td className="us-groups">{groupsCell(u)}</td>
                <td>{twoFactorCell(u)}</td>
                <td>
                  <AccessCell
                    reach={reachOf(u.id)}
                    total={totalForms}
                    inactive={u.active === false}
                    onOpen={() => setEditor({ kind: 'reach', userId: u.id })}
                  />
                </td>
                <td>
                  <SignInCell user={u} ssoRequired={!!ssoRequired} providerName={teamProvider?.name} allowedDomains={allowed} />
                  {/* So a second superadmin can see a reset has already happened
                      rather than doing it again in the opposite direction. */}
                  {u.password_changed_at ? (
                    <div className="fs-cell-sub faint">
                      password {PASSWORD_ACTOR[u.password_changed_by || 'self']} {ago(u.password_changed_at)}
                    </div>
                  ) : isInvitePending(u) && (
                    <div className="fs-cell-sub faint">invited {ago(u.invited_at!)} · not accepted yet</div>
                  )}
                </td>
                <td style={{ color: 'var(--text-muted)' }}>{u.last_signin_at ? ago(u.last_signin_at) : 'never'}</td>
                <td>
                  <span className={`fs-pill ${u.active !== false ? 'ok' : 'neutral'}`}>
                    {u.active !== false ? 'active' : 'disabled'}
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  {/* Managing anyone is a superadmin's job; changing your own
                      password is nobody else's, so your row has a menu either way. */}
                  {menuFor(u)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </TableCard>

      {editor?.kind === 'add' && (
        <AddUserModal
          ssoRequired={!!ssoRequired}
          providerName={teamProvider?.name}
          onClose={() => setEditor(null)}
          onCreated={(email, invite) => {
            setEditor(null);
            if (invite && !invite.emailed) setHandOver(invite);
            else toast(invite ? invitedMessage(invite) : `Added ${email} — they sign in with ${teamProvider?.name || 'your identity provider'}.`);
            load();
          }}
        />
      )}
      {editor?.kind === 'role' && editing && (
        <ChangeRoleModal
          user={editing} isOwner={isOwner} totalForms={totalForms} reach={reachOf(editing.id)}
          onClose={() => setEditor(null)}
          onSave={(role) => write(() => adminUpdateUser(editing.id, { role }))} />
      )}
      {editor?.kind === 'reach' && editing && (
        <ReachPanel userId={editing.id} onClose={() => setEditor(null)} />
      )}
      {editor?.kind === 'active' && editing && (
        <ActiveModal user={editing} onClose={() => setEditor(null)}
          onConfirm={() => write(() => adminUpdateUser(editing.id, { active: editing.active === false }))} />
      )}
      {editor?.kind === 'password' && editing && (
        <ResetPasswordModal user={editing} canEmail={canEmail} onClose={() => setEditor(null)}
          onReset={(body: PasswordReset) => write(
            () => adminResetUserPassword(editing.id, body),
            'sendLink' in body
              ? `Sent ${editing.email} a link to set a new password.`
              : `Set a new password for ${editing.email} — they are signed out everywhere until they use it.`
          )} />
      )}
      {editor?.kind === 'reset2fa' && editing && (
        <Modal title={`Reset two-factor — ${editing.name || editing.email}`} narrow onClose={() => setEditor(null)}
          footer={(
            <>
              <button className="fs-btn danger"
                onClick={() => write(
                  () => resetUserTwoFactor(editing.id),
                  `Two-factor reset for ${editing.email} — they are signed out everywhere and have been emailed.`
                )}>
                Reset two-factor
              </button>
              <button className="fs-btn neutral" onClick={() => setEditor(null)}>Cancel</button>
            </>
          )}>
          <div className="fs-consequence">
            {editing.email} goes back to password-only sign-in, every session they have is
            signed out, and they get an email saying so. If your account requires two-factor,
            their next sign-in walks them through setting it up again.
          </div>
        </Modal>
      )}
      {editor?.kind === 'mine' && me && (
        <ChangePasswordModal email={me.email} onClose={() => setEditor(null)}
          onDone={() => { setEditor(null); toast('Your password has been changed.'); }} />
      )}
      {handOver && <InviteLinkModal invite={handOver} onClose={() => setHandOver(null)} />}
    </div>
  );
}

/**
 * What this person can actually reach.
 *
 * "every form · bypasses grants" for an Owner or an Admin, a real count for a
 * Member — and a WARN pill when a Member has nothing, because warn in this
 * system means "needs a human", which is exactly what an unshared Member is.
 * Never danger: nothing is broken, somebody just has not shared anything yet.
 *
 * A deactivated Member reads "kept while deactivated", because their grants do
 * survive and reactivating restores what they had.
 */
function AccessCell({ reach, total, inactive, onOpen }: {
  reach: UserReachSummary | null;
  total: number;
  inactive: boolean;
  onOpen: () => void;
}) {
  if (!reach) return <span className="fs-cell-sub faint">—</span>;
  if (reach.everything) {
    return (
      <>
        <span style={{ fontWeight: 600 }}>every form</span>
        <div className="fs-cell-sub">bypasses grants</div>
      </>
    );
  }
  if (!reach.forms && !reach.folders) {
    return (
      <>
        <span className="fs-pill warn">nothing yet</span>
        <div className="fs-cell-sub">
          <a href="#" onClick={(e) => { e.preventDefault(); onOpen(); }}>Share something with them →</a>
        </div>
      </>
    );
  }
  return (
    <>
      <a href="#" style={{ fontWeight: 600 }} onClick={(e) => { e.preventDefault(); onOpen(); }}>
        {reach.forms} of {total} forms
      </a>
      <div className="fs-cell-sub">
        {reach.folders} {reach.folders === 1 ? 'folder' : 'folders'}
        {inactive && ' · kept while deactivated'}
      </div>
    </>
  );
}

/**
 * Derived, not stored: an SSO subject means SSO; an exemption means a password
 * that still works; a password with neither, while SSO is required, means a
 * person who cannot get in — which is a warning, never a silent lockout.
 */
function SignInCell({ user, ssoRequired, providerName, allowedDomains }: {
  user: AdminUser; ssoRequired: boolean; providerName?: string; allowedDomains: string[];
}) {
  const domain = (user.email.split('@')[1] || '').toLowerCase();
  const covered = !allowedDomains.length || allowedDomains.includes(domain);

  if (user.sso_sub) {
    return (
      <div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span className="fs-pill identity">SSO{providerName ? ` · ${providerName}` : ''}</span>
          {user.sso_exempt && (
            <span className="fs-pill caution" title="Keeps a password so a broken IdP cannot lock the account out">
              break-glass
            </span>
          )}
        </div>
      </div>
    );
  }
  if (user.sso_exempt) {
    return (
      <div>
        <span className="fs-pill warn">password · exempt from SSO</span>
        {!covered && <div className="fs-cell-sub">domain not covered by the IdP</div>}
      </div>
    );
  }
  if (ssoRequired) {
    return (
      <div>
        <span className="fs-pill danger">cannot sign in</span>
        <div className="fs-cell-sub bad" style={{ fontWeight: 400, color: 'var(--danger)' }}>
          password only, and password login is off
        </div>
      </div>
    );
  }
  return <span className="fs-pill neutral">password</span>;
}

/**
 * The invite. There is no password field, and no way to add one: the person is
 * emailed a link and chooses their own, so nobody in this dialog ever holds
 * their colleague's credential.
 *
 * At the seat ceiling the fields stay VISIBLE and inert rather than
 * disappearing — the block below carries the number and the way out. The
 * ceiling lives only in the refusal itself (`GET /api/admin/billing` is
 * superadmin-only and inventing the number client-side would lie the day a
 * limit changes), so the block appears the moment an invite is refused.
 */
function AddUserModal({ ssoRequired, providerName, onClose, onCreated }: {
  ssoRequired: boolean;
  providerName?: string;
  onClose: () => void;
  onCreated: (email: string, invite: SentInvite | null) => void;
}) {
  // A new person lands on Member: the rung that grants nothing is the safe
  // default, and widening somebody is a decision, not an omission.
  const [draft, setDraft] = useState({ email: '', name: '', role: 'member' as Role });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [seatLimit, setSeatLimit] = useState<PlanLimitBody | null>(null);

  async function add() {
    setError('');
    if (!draft.email) { setError('An email address is required — it is where the invitation goes.'); return; }
    setBusy(true);
    try {
      const r = await adminCreateUser(draft);
      onCreated(draft.email, r.invite);
    } catch (e: any) {
      const limit = planLimitOf(e);
      if (limit) setSeatLimit(limit);
      else setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Invite a user" narrow onClose={onClose}
      footer={(
        <>
          <button className={`fs-btn primary${seatLimit ? ' lim-btn-inert' : ''}`} disabled={busy} onClick={add}>
            {busy ? 'Sending…' : 'Send invitation'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}>
      {error && <div className="ad-err">{error}</div>}
      <div className={seatLimit ? 'lim-inert' : undefined} aria-disabled={seatLimit ? true : undefined}>
        <div className="fs-row-2">
          <div className="fs-field">
            <label>Email</label>
            <input type="email" autoFocus placeholder="name@company.com" value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
          </div>
          <div className="fs-field">
            <label>Name</label>
            <input type="text" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <div className="fs-field" style={{ maxWidth: 260 }}>
            <label>Role</label>
            <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value as Role })}>
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label} — {r.blurb}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="fs-consequence" style={{ marginTop: 16 }}>
          {ssoRequired
            ? <>SSO is required — they sign in with {providerName || 'your identity provider'} on first visit, so there is no invitation to send and no password to set.</>
            : 'They get an email with a link to choose their own password. Nobody here sets it, and until they use the link the login exists but cannot be signed in to.'}
          {draft.role === 'member' && (
            <div style={{ marginTop: 8 }}>
              A Member sees nothing until a folder or a form is shared with them.
            </div>
          )}
        </div>
      </div>
      {seatLimit && (
        <div style={{ marginTop: 14 }}>
          <PlanLimitBlock body={seatLimit} />
        </div>
      )}
    </Modal>
  );
}

/**
 * Change role — three radios, and a consequence sentence with a real number.
 *
 * "Maria stops seeing 38 of the 40 forms in this account" is computed from
 * what she can reach today, never estimated: moving somebody down the ladder
 * is the moment a wall appears, and a sentence that guessed the height of it
 * would be the last one anybody read.
 */
function ChangeRoleModal({ user, isOwner, totalForms, reach, onClose, onSave }: {
  user: AdminUser;
  isOwner: boolean;
  totalForms: number;
  reach: UserReachSummary | null;
  onClose: () => void;
  onSave: (role: Role) => void;
}) {
  const [role, setRole] = useState<Role>((user.role as Role) || 'member');
  const changed = role !== user.role;
  const first = (user.name || user.email).split(/[\s@]/)[0];
  // What they hold today: an Owner or Admin reaches everything, so demoting
  // them costs the difference between everything and their grants.
  const keeps = reach?.everything ? 0 : (reach?.forms ?? 0);

  return (
    <Modal title={`Change role — ${user.name || user.email}`} narrow onClose={onClose}
      footer={(
        <>
          <button className="fs-btn primary" disabled={!changed} onClick={() => onSave(role)}>Save role</button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}>
      {ROLES.map((r) => {
        // Only an Owner may mint an Owner; an Admin who could promote
        // themselves is an Owner who has not said so.
        const barred = r.value === 'owner' && !isOwner;
        return (
          <label key={r.value} className={`fs-choice${role === r.value ? ' on' : ''}`} aria-disabled={barred}>
            <input type="radio" checked={role === r.value} disabled={barred}
              onChange={() => setRole(r.value)} />
            <span>
              <b>{r.label}</b>
              <small>{barred ? 'Only an Owner can make another Owner.' : r.blurb}</small>
            </span>
          </label>
        );
      })}

      <div className={`fs-consequence${changed && role === 'member' ? ' warn' : ''}`} style={{ marginTop: 16 }}>
        {!changed ? 'Pick a different role to move this person.' : (
          <>
            <b>{roleLabel(user.role)} → {roleLabel(role)}.</b>{' '}
            {role === 'member' ? (
              <>
                {reach?.everything && totalForms > keeps
                  ? <>{first} stops seeing {totalForms - keeps} of the {totalForms} forms in this account. </>
                  : <>{first} will only see what has been shared with them. </>}
                They keep anything already shared with them directly or through a group,
                and making them an Admin again restores the rest.
              </>
            ) : role === 'owner' ? (
              <>{first} gains billing, the plan, payment methods and API keys, on top of everything an Admin can do.</>
            ) : (
              <>
                {first} reaches every form in the account and can manage users, groups,
                folders and integrations — but not billing.
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function ActiveModal({ user, onClose, onConfirm }: {
  user: AdminUser;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const reactivating = user.active === false;
  return (
    <Modal title={`${reactivating ? 'Reactivate' : 'Deactivate'} — ${user.name || user.email}`} narrow onClose={onClose}
      footer={(
        <>
          <button className={`fs-btn ${reactivating ? 'primary' : 'danger'}`} onClick={onConfirm}>
            {reactivating ? 'Reactivate' : 'Deactivate'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}>
      <div className="fs-consequence">
        {reactivating
          ? <>{user.email} can sign in again and takes up a seat.</>
          : <>{user.email} cannot sign in until reactivated. Nothing they built is deleted, and their seat frees up immediately.</>}
      </div>
    </Modal>
  );
}
